import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import { compose } from 'redux';
import { useTranslation } from 'react-i18next';
import { Flex } from 'nallian-shared-ui/lib/components';
import userGroupService from '../../services/userGroups/userGroupService';
import updateUserGroupService from '../../services/userGroups/updateUserGroupService';
import UserGroupForm from './UserGroupForm';
import { mapFormToUserGroup } from '../../helpers/userGroups/mapFormToUserGroup';
import { mapUserGroupToForm } from '../../helpers/userGroups/mapUserGroupToForm';

const EditUserGroup = ({
  open,
  urlRoot,
  organisationId,
  dispatchUpdateUserGroupPut,
  updateUserGroupFetch,
  dispatchUserGroupGet,
  userGroupFetch,
}) => {
  const { t } = useTranslation();
  const { actionId } = useParams();
  const navigate = useNavigate();

  useEffect(() => dispatchUserGroupGet(organisationId, actionId, t), [
    dispatchUserGroupGet,
    t,
    organisationId,
    actionId,
  ]);

  const handleSubmit = useCallback(
    body =>
      dispatchUpdateUserGroupPut(
        organisationId,
        actionId,
        mapFormToUserGroup(body),
        () => navigate(`${urlRoot}/user-groups`),
        undefined,
        t
      ),
    [dispatchUpdateUserGroupPut, actionId, organisationId, urlRoot, navigate, t]
  );

  const defaultData = useMemo(
    () => !userGroupFetch.pending && mapUserGroupToForm(userGroupFetch.value),
    [userGroupFetch]
  );

  if (!open || !defaultData?.name) return null;

  return (
    <UserGroupForm
      editMode
      open={open}
      dataLoading={userGroupFetch.pending}
      organisationId={organisationId}
      header={
        <Flex>
          {t('editUserGroup')}&nbsp;-&nbsp;{userGroupFetch.value?.name}
        </Flex>
      }
      onSubmit={handleSubmit}
      submitProcessing={updateUserGroupFetch.pending}
      returnRoute={`${urlRoot}/user-groups`}
      cancelRoute={`${urlRoot}/user-groups`}
      defaultData={defaultData || { name: '' }}
    />
  );
};

EditUserGroup.propTypes = {
  dispatchUserGroupGet: PropTypes.func.isRequired,
  userGroupFetch: PropTypes.object.isRequired,
  dispatchUpdateUserGroupPut: PropTypes.func.isRequired,
  updateUserGroupFetch: PropTypes.object.isRequired,
  urlRoot: PropTypes.string.isRequired,
  organisationId: PropTypes.string.isRequired,
  open: PropTypes.bool,
};

EditUserGroup.defaultProps = { open: false };

export default compose(userGroupService, updateUserGroupService)(EditUserGroup);
